<template>
  <v-dialog
    :value="show"
    transition="dialog-bottom-transition"
    persistent
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :width="$store.state.common.extraLargeDialogWidth"
    @input="(val) => $emit('update:show', val)"
  >
    <v-card>
      <v-card-title class="headline pb-2">
        <v-layout>
          <v-icon class="mr-3">
            crop
          </v-icon>
          {{ title }}
        </v-layout>
        <!-- Cancel cropping dialog button -->
        <v-btn
          icon
          flat
          class="ma-0"
          :title="$t('close')"
          @click="abort()"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <!-- Cropping Tools -->
      <v-layout v-if="!successfullyCropped">
        <v-layout
          justify-center
          wrap
          class="pb-3"
        >
          <div
            v-if="!cropRatio"
            class="image-btn-group mt-2"
          >
            <!-- Landscape -->
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                icon
                :color="targetIsLandscape ? 'msr--text' : null"
                @click="
                  targetRatio = landscapeRatio;
                  croppingOptionClicked = true;
                "
              >
                <v-icon>crop_landscape</v-icon>
              </v-btn>
              <span>16:9</span>
            </v-tooltip>
            <!-- Portrait -->
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                icon
                :color="targetIsPortrait ? 'msr--text' : null"
                @click="
                  targetRatio = portraitRatio;
                  croppingOptionClicked = true;
                "
              >
                <v-icon>crop_portrait</v-icon>
              </v-btn>
              <span>9:16</span>
            </v-tooltip>
          </div>
          <div class="image-btn-group mt-2">
            <!-- Zoom out -->
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                icon
                @click="
                  cropper.zoom(-0.1);
                  croppingOptionClicked = true;
                "
              >
                <v-icon>zoom_out</v-icon>
              </v-btn>
              <span>{{ $t('zoomOutImageCrop') }}</span>
            </v-tooltip>
            <!-- Zoom in -->
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                icon
                @click="
                  cropper.zoom(0.1);
                  croppingOptionClicked = true;
                "
              >
                <v-icon>zoom_in</v-icon>
              </v-btn>
              <span>{{ $t('zoomInImageCrop') }}</span>
            </v-tooltip>
          </div>
          <div class="image-btn-group mt-2">
            <!-- Rotate 90 degrees -->
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                icon
                @click="
                  cropper.rotate(90);
                  croppingOptionClicked = true;
                "
              >
                <v-icon style="transform: scaleX(-1)">
                  rotate_90_degrees_ccw
                </v-icon>
              </v-btn>
              <span>{{ $t('rotateImageCrop') }}</span>
            </v-tooltip>
            <!-- Mirror horizontal -->
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                icon
                @click="
                  flipHorizontal *= -1;
                  cropper.scaleX(flipHorizontal);
                  croppingOptionClicked = true;
                "
              >
                <v-icon style="transform: rotateZ(90deg)">
                  height
                </v-icon>
              </v-btn>
              <span>{{ $t('mirrorHorizontalImageCrop') }}</span>
            </v-tooltip>
            <!-- Mirror vertical -->
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                icon
                @click="
                  flipVertical *= -1;
                  cropper.scaleY(flipVertical);
                  croppingOptionClicked = true;
                "
              >
                <v-icon>height</v-icon>
              </v-btn>
              <span>{{ $t('mirrorVerticalImageCrop') }}</span>
            </v-tooltip>
          </div>
          <div class="image-btn-group mt-2">
            <!-- Background color -->
            <v-menu
              v-model="colorpickerMenuModel"
              :close-on-content-click="false"
              offset-y
            >
              <v-btn
                slot="activator"
                icon
                :title="$t('chooseBackgroundColor')"
                @click="croppingOptionClicked = true"
              >
                <v-icon
                  color="black"
                  style="
                    position: absolute;
                    clip-path: polygon(0 0, 100% 0, 100% 75%, 0 75%);
                  "
                >
                  format_color_fill
                </v-icon>
                <v-icon
                  :color="
                    backgroundColor === 'transparent'
                      ? 'black'
                      : backgroundColor
                  "
                  style="
                    position: absolute;
                    clip-path: polygon(0% 75%, 100% 75%, 100% 100%, 0% 100%);
                  "
                >
                  format_color_fill
                </v-icon>
              </v-btn>
              <v-card>
                <v-card-text>
                  <color-picker
                    :key="colorpickerMenu"
                    :color="backgroundColor"
                    show-color-palette
                    @changed="(color) => (backgroundColor = color)"
                    @close="colorpickerMenu = false"
                  />
                </v-card-text>
              </v-card>
            </v-menu>
            <!-- Saturation, focus -->
            <v-menu
              v-model="imageFilterMenu"
              :close-on-content-click="false"
              offset-y
            >
              <v-btn
                slot="activator"
                icon
                :title="$t('imageFilters')"
                :class="someFilterUsed ? 'msr--text' : ''"
                @click="croppingOptionClicked = true"
              >
                <v-icon>auto_awesome</v-icon>
              </v-btn>
              <v-list class="no-scrollbar">
                <v-list-tile>
                  <v-layout column>
                    <div class="slider-label">
                      {{ $t('imageFilterSaturation') }}
                    </div>
                    <v-slider
                      v-model="saturation"
                      min="0"
                      max="100"
                      hide-details
                      class="mt-0"
                      prepend-icon="tonality"
                      @click:prepend="onSaturationSliderIconClicked"
                    />
                  </v-layout>
                </v-list-tile>
                <v-divider class="my-2" />
                <v-list-tile>
                  <v-layout column>
                    <div class="slider-label">
                      {{ $t('imageFilterFocus') }}
                    </div>
                    <v-slider
                      v-model="focus"
                      min="0"
                      max="100"
                      hide-details
                      class="mt-0"
                      prepend-icon="blur_on"
                      @click:prepend="onFocusSliderIconClicked"
                    />
                  </v-layout>
                </v-list-tile>
                <v-divider class="my-2" />
                <v-list-tile>
                  <v-layout column>
                    <div class="slider-label">
                      {{ $t('imageFilterBrightness') }}
                    </div>
                    <v-slider
                      v-model="brightness"
                      min="0"
                      max="100"
                      hide-details
                      class="mt-0"
                      prepend-icon="light_mode"
                      @click:prepend="onBrightnessSliderIconClicked"
                    />
                  </v-layout>
                </v-list-tile>
              </v-list>
            </v-menu>
          </div>
        </v-layout>
      </v-layout>
      <!-- Cropping Image Container -->
      <v-card-text class="pt-1">
        <v-layout
          justify-center
          class="image-cropper-root"
          :class="[cropperInstanceClass]"
          @click="cropperClicked = true"
        >
          <!-- FIXME: Filters are applied to cropperjs controls as well,
            like the drag box being blurred etc. which makes it hard to
            use.
          -->
          <div style="max-width: 100%">
            <!-- Input image to crop -->
            <div :style="croppedImagePreviewFilterStyle">
              <!-- Brightness overlay for live preview -->
              <div
                v-show="imageFilepath && !successfullyCropped"
                class="custom-brightness-overlay-preview"
                :style="{'background-color': brightnessOverlayColor}"
              />
              <img
                v-show="imageFilepath && !successfullyCropped"
                ref="inputImg"
                style="z-index: 1"
                :style="imgStyle"
                :src="imageFilepath"
              >
            </div>
            <!-- Cropping result image preview -->
            <img
              v-show="imageFileCropped && successfullyCropped"
              style="outline: 1px solid rgba(0, 0, 0, 0.16)"
              :style="imgStyle"
              :src="imageFileCroppedPath"
            >
          </div>
        </v-layout>
      </v-card-text>
      <v-card-actions class="pb-3">
        <v-layout
          justify-end
          class="mr-2"
        >
          <!-- Go back to cropping -->
          <v-btn
            v-if="successfullyCropped"
            :title="$t('goBack')"
            round
            flat
            color="grey darken-3"
            @click="goBackToCropping()"
          >
            <v-icon left>
              arrow_back
            </v-icon>
            {{ $t('goBack') }}
          </v-btn>
          <!-- Full Reset -->
          <v-btn
            v-if="dirty && !successfullyCropped"
            round
            flat
            color="grey darken-3"
            @click="reset(true)"
          >
            <v-icon left>
              refresh
            </v-icon>
            {{ $t('reset') }}
          </v-btn>
          <!-- Apply cropping -->
          <btn-ok
            v-if="!successfullyCropped"
            :title="$t('goForward')"
            :label="$t('preview')"
            label-with-icon
            icon="arrow_forward"
            round
            @click="crop()"
          />
          <!-- Confirm cropped image and move on -->
          <btn-ok
            v-else
            :title="$t('confirm')"
            :label="$t('confirm')"
            label-with-icon
            icon="check"
            round
            @click="save()"
          />
        </v-layout>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Cropper from 'cropperjs';

import {generateRandomId, mapRange} from '@/utils';

import BtnOk from '@/components/ButtonOk';
import ColorPicker from '@/components/ColorPicker';

const BRIGHTNESS_MIN = 0;
const BRIGHTNESS_DEFAULT = 50;
const BRIGHTNESS_MAX = 100;

const FOCUS_MAX = 100;
const SATURATION_MAX = 100;

const defaults = {
  brightness: BRIGHTNESS_DEFAULT,
  backgroundColor: '#000000',
  colorpickerMenu: false,
  cropper: null,
  cropperClicked: false,
  cropperDragMode: 'move', // Either 'crop' or 'move'.
  croppingOptionClicked: false,
  flipHorizontal: 1,
  flipVertical: 1,
  focus: FOCUS_MAX,
  imageFileCropped: null,
  imageFileCroppedPath: null,
  imageFilterMenu: false,
  landscapeRatio: 16 / 9,
  portraitRatio: 9 / 16,
  saturation: SATURATION_MAX,
  successfullyCropped: false,
  targetRatio: null,
};

export default {
  components: {
    BtnOk,
    ColorPicker,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: function () {
        return this.$t('cropImage');
      },
    },
    imageFilepath: {
      type: String,
      required: true,
    },
    /**
     * Original image filename, passed so we can use it for the output.
     */
    imageFilename: {
      type: String,
      required: true,
    },
    imageWidth: {
      type: Number,
      required: true,
    },
    imageHeight: {
      type: Number,
      required: true,
    },
    /**
     * Note: We used to limit this to mode 3 when cropRatio was given,
     *   so that zooming out was disabled and the result would always
     *   fit the given crop box, but that actually limits some use cases
     *   where you want to capture a zoomed out image with some
     *   background/transparency around it.
     *
     *   For some use cases like MenuItem images, viewMode=3 still works
     *   better as it helps avoid cropping slim lines of transparency at
     *   the edges by accident.
     */
    viewMode: {
      type: Number,
      default: 0,
    },
    /**
     * Allow passing crop box ratio from outside.
     *
     * When this is specified, the landscape/portrait buttons will not
     * be available, as we are commited to the given ratio instead.
     *
     * If not specified, will fallback to either landscape or portrait
     * based on what is closer to the actual image ratio.
     */
    cropRatio: {
      type: [Number, String],
      default: null,
      validator: function (cropRatio) {
        if (cropRatio === undefined || cropRatio === null) return true;
        if (cropRatio === 'any') return true;
        return !isNaN(cropRatio);
      },
    },
    /**
     * Allow overriding the initial fill color'.
     */
    initialBackgroundColor: {
      type: String,
      default: 'transparent',
    },
  },
  data() {
    return {
      backgroundColor: this.initialBackgroundColor,

      brightness: defaults.brightness,
      colorpickerMenu: defaults.colorpickerMenu,
      cropper: defaults.cropper,
      cropperClicked: defaults.cropperClicked,
      cropperDragMode: defaults.cropperDragMode,
      croppingOptionClicked: defaults.croppingOptionClicked,
      flipHorizontal: defaults.flipHorizontal,
      flipVertical: defaults.flipVertical,
      focus: defaults.focus,
      imageFilterMenu: defaults.imageFilterMenu,
      landscapeRatio: defaults.landscapeRatio,
      portraitRatio: defaults.portraitRatio,
      saturation: defaults.saturation,

      imageFileCropped: defaults.imageFileCropped,
      imageFileCroppedPath: defaults.imageFileCroppedPath,
      successfullyCropped: defaults.successfullyCropped,
      targetRatio: defaults.targetRatio,
    };
  },
  computed: {
    /**
     * Create a unique class for this cropper instance to select its child elements.
     */
    cropperInstanceClass() {
      return 'cropper-instance-' + generateRandomId();
    },
    cropperContainerSelector() {
      return '.' + this.cropperInstanceClass + ' .cropper-container';
    },
    dirty() {
      return this.cropperClicked || this.croppingOptionClicked;
    },
    saturationFilterUsed() {
      return this.saturation != defaults.saturation;
    },
    focusFilterUsed() {
      return this.focus != defaults.focus;
    },
    brightnessFilterUsed() {
      return this.brightness != defaults.brightness;
    },
    someFilterUsed() {
      return (
        this.saturationFilterUsed ||
        this.focusFilterUsed ||
        this.brightnessFilterUsed
      );
    },
    contextFilterGrayscale() {
      // Named 'Saturation' in the UI.
      return 'grayscale(' + (SATURATION_MAX - this.saturation) + '%)';
    },
    contextFilterBlur() {
      // Named 'Focus' in the UI.
      const maxBlurPx = 15;
      const blurValue = (FOCUS_MAX - this.focus) * (maxBlurPx / 100);
      // FIXME: We seem to produce a much stronger blur in the
      //   scaled-down live preview than in the actual image result.
      return 'blur(' + blurValue / 10.0 + 'rem)';
    },
    croppedImagePreviewFilterExpressionString() {
      // Note: 'Brightness' is not applied here, but when the canvas context is drawn.
      return this.contextFilterGrayscale + ' ' + this.contextFilterBlur;
    },
    /**
     * Fix colorpicker not showing updated color by delaying one tick.
     */
    colorpickerMenuModel: {
      get() {
        return this.colorpickerMenu;
      },
      set(menu) {
        // Switch from the initial transparent to white once the picker
        // is displayed so that the color wheel is ready to pick from.
        if (menu && this.backgroundColor === 'transparent') {
          this.backgroundColor = '#ffffff';
        }
        this.$nextTick(() => (this.colorpickerMenu = menu));
      },
    },
    aspectRatio() {
      return this.imageWidth / this.imageHeight;
    },
    isPortrait() {
      return this.imageHeight > this.imageWidth;
    },
    isLandscape() {
      return !this.isPortrait;
    },
    targetIsLandscape() {
      return this.targetRatio === this.landscapeRatio;
    },
    targetIsPortrait() {
      return this.targetRatio === this.portraitRatio;
    },
    /**
     * Width is limited by device size up to a fixed value.
     */
    maxDialogWidth() {
      const max = this.$vuetify.breakpoint.xsOnly ? 480 : 720;
      if (this.$vuetify.breakpoint.width < max) {
        return this.$vuetify.breakpoint.width;
      }
      return max;
    },
    /**
     * Height is limited by device size up to a fixed value.
     */
    maxDialogHeight() {
      const max = this.$vuetify.breakpoint.xsOnly ? 360 : 560;
      if (this.$vuetify.breakpoint.height < max) {
        return this.$vuetify.breakpoint.height;
      }
      return max;
    },
    dialogDimensions() {
      const availableWidth = this.maxDialogWidth;
      const availableHeight = this.maxDialogHeight;

      let width, height;
      if (this.isLandscape) {
        width = availableWidth;
        height = width * this.aspectRatio;
        if (height > availableHeight) {
          height = availableHeight;
        }
      } else if (this.isPortrait) {
        height = availableHeight;
        width = height / this.aspectRatio;
        if (width > availableWidth) {
          width = availableWidth;
        }
      }
      return {width: width, height: height};
    },
    imgStyle() {
      return {
        'background-color': this.backgroundColor,
        'max-width': '100%',
        'max-height': this.dialogDimensions.height + 'px',
      };
    },
    /** 'Fake' desaturation, blur etc. in the frontend preview.
     *
     * This does not actually desaturate the cropped image yet, but
     * visualizes the effect.
     */
    croppedImagePreviewFilterStyle() {
      const filters = {
        '-webkit-filter': this.croppedImagePreviewFilterExpressionString,
        '-moz-filter': this.croppedImagePreviewFilterExpressionString,
        '-ms-filter': this.croppedImagePreviewFilterExpressionString,
        '-o-filter': this.croppedImagePreviewFilterExpressionString,
      };
      return filters;
    },
    brightnessOverlayColor() {
      const fadeToBlack = this.brightness < BRIGHTNESS_DEFAULT;
      const color = fadeToBlack ? 0 : 255;

      const lowerLimit = fadeToBlack ? BRIGHTNESS_MIN : BRIGHTNESS_DEFAULT;
      const upperLimit = fadeToBlack ? BRIGHTNESS_DEFAULT : BRIGHTNESS_MAX;

      const minAlpha = fadeToBlack ? 1.0 : 0;
      const maxAlpha = fadeToBlack ? 0 : 1.0;
      const alpha = mapRange(
        lowerLimit,
        upperLimit,
        minAlpha,
        maxAlpha,
        this.brightness
      );

      return `rgba(${color}, ${color}, ${color}, ${alpha})`;
    },
  },
  watch: {
    show: function (showDialog) {
      if (showDialog) {
        const full = true;
        this.reset(full);
      }
    },
    targetRatio: function (ratio) {
      if (this.cropper) {
        this.cropper.setAspectRatio(ratio);
      }
    },
    colorpickerMenu: function () {
      if (this.colorpickerMenu && this.backgroundColor === 'transparent') {
        this.backgroundColor = '#ffffff';
      }
    },
    backgroundColor: function () {
      this.applyDynamicCropperStyleOverride();
    },
  },
  mounted() {
    this.initializeCropperJS();
  },
  methods: {
    onBrightnessSliderIconClicked() {
      if (this.brightness == BRIGHTNESS_MIN) {
        this.brightness = BRIGHTNESS_DEFAULT;
      } else if (this.brightness <= BRIGHTNESS_DEFAULT) {
        this.brightness = BRIGHTNESS_MAX;
      } else if (this.brightness <= BRIGHTNESS_MAX) {
        this.brightness = BRIGHTNESS_MIN;
      }
    },
    onSaturationSliderIconClicked() {
      if (this.saturation == 0) {
        this.saturation = SATURATION_MAX / 2;
      } else if (this.saturation <= SATURATION_MAX / 2) {
        this.saturation = SATURATION_MAX;
      } else if (this.saturation <= SATURATION_MAX) {
        this.saturation = 0;
      }
    },
    onFocusSliderIconClicked() {
      if (this.focus == 0) {
        this.focus = FOCUS_MAX / 2;
      } else if (this.focus <= FOCUS_MAX / 2) {
        this.focus = FOCUS_MAX;
      } else if (this.focus <= FOCUS_MAX) {
        this.focus = 0;
      }
    },
    applyFiltersToCanvas(canvas) {
      // https://stackoverflow.com/a/67392978
      const ctx = canvas.getContext('2d');

      // Draw image with filters applied.
      //
      // FIXME: For Safari, the CanvasRenderingContext2D.filter API is
      //   polyfilled (see main.js), and has the limitation that filters
      //   cannot be combined correctly,
      //   as it seems.
      ctx.filter = this.croppedImagePreviewFilterExpressionString;
      ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);

      // Apply 'brightness overlay'. We were not happy with the builtin
      // css brightness() filter results and opted to just do a
      // black/white fade manually instead.
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = this.brightnessOverlayColor;
      ctx.fill();
    },
    applyDynamicCropperStyleOverride() {
      // Destroy prior style definition.
      const identifierClass = 'cropperjs-style-overrides';
      const styleElements = document.querySelectorAll('.' + identifierClass);
      styleElements.forEach((elem) => {
        elem.parentNode.removeChild(elem);
      });

      // Add new style definition to the DOM.
      const css = `
				.image-cropper-root .cropper-wrap-box,
				.image-cropper-root .cropper-modal {
					background-color: ${this.backgroundColor};
				}
			`;
      const style = document.createElement('style');
      style.type = 'text/css';
      document.head.appendChild(style);
      style.innerHTML = css;
    },
    getInputImageElement() {
      return this.$refs.inputImg;
    },
    /**
     * Edge doesn't support canvas.toBlob, so we polyfill it here.
     *
     * Taken from: https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob
     */
    polyfillCanvasToBlob() {
      if (HTMLCanvasElement.prototype.toBlob) {
        return;
      }
      console.log('Installing polyfill for canvas.toBlob()');
      Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
        value: function (callback, type, quality) {
          var canvas = this;
          setTimeout(function () {
            let binStr = atob(canvas.toDataURL(type, quality).split(',')[1]);
            let len = binStr.length;
            let arr = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
              arr[i] = binStr.charCodeAt(i);
            }
            callback(new Blob([arr], {type: type || 'image/png'}));
          });
        },
      });
    },
    initializeCropperJS() {
      this.polyfillCanvasToBlob();

      const img = this.getInputImageElement();

      // By default the crop box will be constrained to either 16:9 or 9:16,
      // unless we pass a desired cropRatio specifically. The special value
      // of 'any' means there's no constrain at all, crop in any ratio.
      if (this.cropRatio) {
        if (this.cropRatio === 'any') {
          this.targetRatio = null;
        } else {
          this.targetRatio = this.cropRatio;
        }
      } else {
        this.targetRatio = this.isLandscape
          ? this.landscapeRatio
          : this.portraitRatio;
      }

      this.cropper = new Cropper(img, {
        viewMode: this.viewMode,

        // 1.0 = 100%, but sometimes it doesn't fully fill. If we
        // increase this to e.g. 1.1 however, it will often add a bit of
        // empty space which is easily overlooked. We tried both, so no
        // real silver bullet here for now.
        autoCropArea: this.viewMode == 3 ? 1.0 : 0.98,

        aspectRatio: this.targetRatio,
        background: true,
        dragMode: this.cropperDragMode,
        fillColor: this.backgroundColor,
        toggleDragModeOnDblclick: false,
      });

      this.applyDynamicCropperStyleOverride();
    },
    resetCropperFully() {
      this.destroyCropper();
      this.initializeCropperJS();
    },
    crop() {
      // Prevent upscaling an image too much which has been zoomed-out,
      // as it does not yield any image quality improvement, but a
      // possibly much larger upload size. Full-HD may not be enough for
      // e.g. Adobe Animate images, so we allow somewhat larger images.
      const options = {
        maxWidth: 2048,
        maxHeight: 2048,
        fillColor: this.backgroundColor,
      };

      const canvas = this.cropper.getCroppedCanvas(options);

      // Apply additional image modifications here.
      if (this.someFilterUsed) {
        this.applyFiltersToCanvas(canvas);
      }

      canvas.toBlob((blob) => {
        // Since Edge does not support 'new File()', we instead use the
        // Blob directly here as a workaround. This requires us to also
        // pass the filename during upload separately, see api.js >
        // uploadImage().
        blob.name = this.imageFilename;

        this.imageFileCropped = blob;

        const url = URL.createObjectURL(blob);
        this.imageFileCroppedPath = url;
        this.successfullyCropped = true;

        this.hideCropperContainerElement();
      }, 'image/png');
    },
    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy();
      }
      this.cropper = null;
    },
    reset(full = false) {
      // Some cropping state on top of cropperjs internal things.
      this.backgroundColor = this.initialBackgroundColor;

      this.brightness = defaults.brightness;
      this.cropperClicked = defaults.cropperClicked;
      this.cropperDragMode = defaults.cropperDragMode;
      this.croppingOptionClicked = defaults.croppingOptionClicked;
      this.flipHorizontal = defaults.flipHorizontal;
      this.flipVertical = defaults.flipVertical;
      this.focus = defaults.focus;
      this.saturation = defaults.saturation;
      this.targetRatio = defaults.targetRatio;

      // Reset values that indicate we already cropped.
      this.imageFileCropped = defaults.imageFileCropped;
      this.imageFileCroppedPath = defaults.imageFileCroppedPath;
      this.successfullyCropped = defaults.successfullyCropped;

      // If desired, destroy and recreate the cropperjs instance. This
      // helps avoiding broken situations when Vue's garbage collector
      // conflicts with how cropperjs manages its own instance. Should
      // be done e.g. when re-opening this dialog.
      if (full) {
        this.$nextTick(() => {
          this.resetCropperFully();
        });
      }
    },
    /**
     * Continue cropping with cropperjs remembering its previous state.
     */
    goBackToCropping() {
      this.imageFileCropped = defaults.imageFileCropped;
      this.imageFileCroppedPath = defaults.imageFileCroppedPath;
      this.successfullyCropped = defaults.successfullyCropped;

      this.showCropperContainerElement();
    },
    abort() {
      this.reset();
      this.$emit('update:show', false);
      this.$emit('aborted');
    },
    save() {
      this.$emit('update:show', false);
      this.$emit('saved', this.imageFileCropped);
    },
    hideCropperContainerElement() {
      const container = document.querySelector(this.cropperContainerSelector);
      container.style.display = 'none';
    },
    showCropperContainerElement() {
      const container = document.querySelector(this.cropperContainerSelector);
      container.style.display = 'block';
    },
  },
};
</script>

<style scoped>
@import '../../node_modules/cropperjs/dist/cropper.min.css';

.slider-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
}

.v-menu__content >>> .no-scrollbar {
  overflow-y: hidden;
}

.image-btn-group {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  margin-right: 6px;
}
.image-btn-group:last-of-type {
  margin-right: 0px;
}
</style>

<style>
.custom-brightness-overlay-preview {
  position: absolute;
  min-width: 100%;
  min-height: 100%;
  z-index: 2; /* Must be more than the img it overlays. */
  pointer-events: none; /* Let click/drag pass through to cropperjs. */
}

.cropper-container.cropper-bg {
  /* Fix Vuetify's CSS somehow breaks the checker pattern in cropperjs's background. */
  background-repeat: initial !important;
}
</style>



// WEBPACK FOOTER //
// src/components/ImageCropDialog.vue